import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState,
} from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  makeStyles,
  FormControl,
  FormHelperText,
  Typography,
  ListItemText,
  ListItem,
  Avatar,
  TextField,
} from '@material-ui/core';
import { find } from 'lodash';

import {
  getEventTemplate,
  resetEventTemplate,
} from '@pingum/app-state-management/eventTemplate/actions';
import styles from './styles';

import StatusBadge from '../StatusBadge';
import UserMentionTextField from '../UserMentionTextField';
import noChangeStatus from '../../pages/EventDetailPage/AddCommentCard/noChangeStatus';
import UserDownshift from '../../forms/UserDownshift';

// utils
import {
  getColorForItem,
  getIconForItem,
} from '../../../utils/iconHelper';

const useStyles = makeStyles(styles);

const getStatusItemSecondaryText = (status, eventStatus) => {
  if ((eventStatus && eventStatus.status) && status.id === eventStatus.status.id) {
    return 'Current event status';
  }
  if (status.endingStatus) {
    return 'Autocompletes the event';
  }
  return '';
};

export const StatusAssignmentComponent = ({
  allowNoStatusChange,
  showCurrentStatusBanner,
  showStatusCommentField,
  selectedStatus,
  event,
  comment,
  eventStatus,
  isValidStatus,
  isValidAssignment,
  assignment,
  handleSelectedStatusChange,
  handleWorkflowStatusChange,
  handleCommentChange,
  handleAssignmentChange,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    isFetchingEventTemplate,
    eventTemplate,
  } = useSelector((state) => state.eventTemplate);
  const { statuses } = useSelector((state) => state.status);

  const [nextWorkflowStatus, setNextWorkflowStatus] = useState(null);
  const [allowedStatusChanges, setAllowedStatusChanges] = useState(statuses);

  // useEffect(() => {
  //   // load statuses if not already loaded
  //   if (!statuses || statuses.length === 0) {
  //     dispatch(getStatuses(false));
  //   }
  // }, []);

  useEffect(() => {
    if ((event && event.eventTemplateId) && (!eventTemplate.id || eventTemplate.id !== event.eventTemplateId)) {
      dispatch(getEventTemplate(event.eventTemplateId));
    } else if (eventTemplate.id) {
      dispatch(resetEventTemplate());
    }
  }, [event.eventTemplateId]);

  useEffect(() => {
    if (!eventTemplate || !eventTemplate.workflowStatuses || eventTemplate.workflowStatuses.length === 0) {
      return setAllowedStatusChanges(statuses);
    }
    // if we have an event and that event has a template, let's grab the associated workflow status
    const currentWorkflowStatus = (
      event
        && event.eventTemplateId
        && eventStatus
        && eventStatus.status
        && eventTemplate
        && eventTemplate.id
        && eventTemplate.workflowStatuses
    )
      ? (eventTemplate.workflowStatuses.find((workflowStatus) => workflowStatus.statusId === eventStatus.status.id) || null)
      : null;

    // filter to only allowed statuses if we have a woflow status
    return setAllowedStatusChanges(
      !!currentWorkflowStatus && currentWorkflowStatus.nextStatusOptions
        ? statuses.filter((status) => currentWorkflowStatus.nextStatusOptions.indexOf(status.id) >= 0)
        : eventTemplate.workflowStatuses.map((workflowStatus) => {
          const foundStatus = find(statuses, (status) => status.id === workflowStatus.statusId);
          return {
            ...foundStatus,
            statusOrder: workflowStatus.order,
          };
        }),
    );
  }, [eventTemplate, statuses]);

  useEffect(() => {
    // let's set the next workflow status based on status selection
    if (
      selectedStatus
        && selectedStatus.id
        && eventTemplate
        && eventTemplate.id
        && eventTemplate.workflowStatuses
    ) {
      const selectedWorkflowStatus = eventTemplate.workflowStatuses.find((workflowStatus) => workflowStatus.statusId === selectedStatus.id);
      setNextWorkflowStatus(selectedWorkflowStatus || null);
      // trigger workflow status change to parent - used in validation of parent
      handleWorkflowStatusChange(selectedWorkflowStatus || null);
    } else {
      setNextWorkflowStatus(null);
      handleWorkflowStatusChange(null);
    }
  }, [eventTemplate, selectedStatus]);

  return (
    <div className={classes.container}>
      {
        showCurrentStatusBanner
          && (
            <ListItem className={classes.eventListItem}>
              <Avatar>
                <StatusBadge
                  icon={
                    getIconForItem(statuses, (eventStatus && eventStatus.status)
                      ? eventStatus.status.id
                      : '')
                  }
                  iconColor={
                    getColorForItem(statuses, (eventStatus && eventStatus.status)
                      ? eventStatus.status.id
                      : '')
                  }
                />
              </Avatar>
              <ListItemText
                primary={event.name}
                secondary={
                  (
                    eventStatus
                      && eventStatus.status
                  )
                    ? eventStatus.status.name
                    : ''
                }
              />
            </ListItem>
          )
      }
      <FormControl
        fullWidth
        variant="outlined"
      >
        <TextField
          select
          label="Status"
          name="status"
          id="status-select"
          variant="outlined"
          fullWidth
          error={!isValidStatus && selectedStatus.id < 0}
          disabled={isFetchingEventTemplate}
          value={selectedStatus.id}
          onChange={(e) => {
            const newSelectedStatus = find(allowedStatusChanges, (allowedStatus) => allowedStatus.id === e.target.value) || noChangeStatus;
            handleSelectedStatusChange(newSelectedStatus, nextWorkflowStatus);
          }}
          SelectProps={{
            renderValue: () => (
              <StatusBadge
                icon={selectedStatus.icon}
                iconColor={selectedStatus.iconColor}
                label={selectedStatus.name}
              />
            ),
            classes: { select: classes.input },
          }}
        >
          {
            allowNoStatusChange && (
              <ListItem
                key="status-no-change"
                value={noChangeStatus.id}
                button
              >
                <Avatar>
                  <StatusBadge
                    icon={noChangeStatus.icon}
                    iconColor={noChangeStatus.iconColor}
                  />
                </Avatar>
                <ListItemText primary={noChangeStatus.name} />
              </ListItem>
            )
          }
          {
            allowedStatusChanges.sort((a, b) => a.statusOrder - b.statusOrder).map((status) => (
              <ListItem
                key={`status-${status.id}`}
                value={status.id}
                button={!(eventStatus && eventStatus.status)
                  || ((eventStatus && eventStatus.status) && eventStatus.status.id !== status.id)}
                disabled={(eventStatus && eventStatus.status)
                  && eventStatus.status.id === status.id}
              >
                <Avatar>
                  <StatusBadge
                    icon={status.icon}
                    iconColor={status.iconColor}
                  />
                </Avatar>
                <ListItemText
                  primary={status.name}
                  secondary={getStatusItemSecondaryText(status, eventStatus)}
                />
              </ListItem>
            ))
          }
        </TextField>
        {
          selectedStatus.id < 0
            && (
              <FormHelperText>
                <Typography variant="caption" gutterBottom>
                  No status selected, this will leave only a comment.
                </Typography>
              </FormHelperText>
            )
        }
        {
          selectedStatus.endingStatus
            && (
              <FormHelperText>
                <Typography variant="caption" gutterBottom>
                  This is an <b>ending status</b>. The event will be completed.
                </Typography>
              </FormHelperText>
            )
        }
      </FormControl>
      {
        selectedStatus.id >= 0 && (
          <UserDownshift
            fieldName="Status Assignment"
            selectedItem={
              assignment
                ? assignment.split(/(?=@\[)/)
                : []
            }
            onChange={(user) => handleAssignmentChange(user, nextWorkflowStatus)}
            onRemoveItem={(user) => handleAssignmentChange(user, nextWorkflowStatus)}
            itemToString={(user) => user.email}
            readonly={false}
            required={nextWorkflowStatus && nextWorkflowStatus.requiredAssignment}
            helperText={!isValidAssignment
              ? 'Assignment required at this status'
              : ''}
            error={!isValidAssignment}
          />
        )
      }
      {
        showStatusCommentField && (
          <div className={classes.commentFormContainer}>
            <UserMentionTextField
              disabled={isFetchingEventTemplate}
              value={comment}
              label="Comment"
              onChange={handleCommentChange}
              required={selectedStatus.requireComment}
              error={!isValidStatus}
              helperText={!isValidStatus
                ? 'A comment is required for this status change'
                : 'Mention people using @ + name/email'}
            />
          </div>
        )
      }
    </div>
  );
};

StatusAssignmentComponent.propTypes = {
  // configs
  allowNoStatusChange: PropTypes.bool,
  showCurrentStatusBanner: PropTypes.bool,
  showStatusCommentField: PropTypes.bool,
  isValidStatus: PropTypes.bool,
  isValidAssignment: PropTypes.bool,
  // data
  comment: PropTypes.string,
  eventStatus: PropTypes.object,
  selectedStatus: PropTypes.object,
  assignment: PropTypes.string,
  event: PropTypes.object,
  // callbacks
  handleSelectedStatusChange: PropTypes.func.isRequired,
  handleWorkflowStatusChange: PropTypes.func.isRequired,
  handleAssignmentChange: PropTypes.func.isRequired,
  handleCommentChange: PropTypes.func,
};

StatusAssignmentComponent.defaultProps = {
  allowNoStatusChange: false,
  showCurrentStatusBanner: false,
  showStatusCommentField: false,
  comment: '',
  selectedStatus: noChangeStatus,
  assignment: null,
  event: {},
  eventStatus: {},
  isValidStatus: true,
  isValidAssignment: true,
  handleCommentChange: () => {},
};

export default StatusAssignmentComponent;
